import type { CubeApi } from '@cubejs-client/core';
import { useCallback, useEffect, useState } from 'react';
import { normalizeCubeMeta, NormalizedCubeCatalog } from './cubeMeta';

export type CubeMetaState = {
  /** Normalized live catalog, or `null` while loading / when `/meta` is unreachable. */
  catalog: NormalizedCubeCatalog | null;
  loading: boolean;
  /** Human-readable error when `/meta` could not be resolved; `null` otherwise. */
  error: string | null;
  /** Explicit refetch of `/meta` (the only trigger besides the initial open). */
  refresh: () => void;
};

/**
 * Resolves Cube's live `/meta` catalog once per builder session and caches it.
 *
 * The builder mounts fresh each time it is opened (it is conditionally rendered
 * by `WidgetLayoutEditor`), so fetching on mount is "once per session". The
 * `cubeApi` client is threaded down from `WidgetLayoutComponent` (a single
 * memoized instance) rather than created here, avoiding a second client. A
 * failed `/meta` surfaces as `error` (never a silent empty catalog) so the Data
 * Source select can render an error state and disable save.
 */
export function useCubeMeta( cubeApi: CubeApi | undefined ): CubeMetaState {
  const [ catalog, setCatalog ] = useState<NormalizedCubeCatalog | null>( null );
  const [ loading, setLoading ] = useState<boolean>( false );
  const [ error, setError ] = useState<string | null>( null );

  const load = useCallback( async () => {
    if ( !cubeApi ) {
      setCatalog( null );
      setError( 'Cube client unavailable' );
      setLoading( false );
      return;
    }
    setLoading( true );
    setError( null );
    try {
      const meta = await cubeApi.meta();
      setCatalog( normalizeCubeMeta( meta.cubes ));
    } catch ( err ) {
      setCatalog( null );
      setError( err instanceof Error ? err.message : String( err ));
    } finally {
      setLoading( false );
    }
  }, [cubeApi]);

  useEffect(() => {
    load();
  }, [load]);

  return { catalog, loading, error, refresh: load };
}
